<!-- Carousel slide template -->
<ng-template #slideTpl let-data let-i="index">
    <article class="carousel-slide" [style.--section]="data.colour">
        <header class="slide-head">
            <span class="slide-swatch" aria-hidden="true"></span>
            <h3>{{ data.title }}</h3>
        </header>
        @if (keyPoint(data); as point) {
            <p class="slide-key"><mat-icon aria-hidden="true">insights</mat-icon><span [innerHTML]="point"></span></p>
        }
        @if (data.description.length) {
            <div class="slide-text">
                @for (d of data.description; track d) {
                    <p [innerHTML]="d"></p>
                }
            </div>
        }
        @if (data.comment) {
            <aside class="slide-comment"><mat-icon aria-hidden="true">comment</mat-icon><span [innerHTML]="data.htmlComment"></span></aside>
        }
        @if (data.visibleCards.length) {
            <app-insight-cards [cards]="data.visibleCards"></app-insight-cards>
        }
        @if (data.type === INSIGHTS_SECTION.ACTIONS && linqset) {
            <mat-card appearance="outlined" class="slide-panel">
                <h4 class="card-title">Action types</h4>
                <mat-card-content class="table-scroll">
                    <app-action-types [insights]="linqset?.insights" [comparison]="comparison?.insights"></app-action-types>
                </mat-card-content>
            </mat-card>
        }
        @if (data.fields.length > 0) {
            <div class="slide-tables">
                @for (t of data.fields; track t.title) {
                    <app-top-five [title]="t.title" [subTitle]="t.subTitle" [rows]="t.rows" [colour]="t.colour" [showAsTable]="true" [includePercentages]="t.includePercentages" [includeUnits]="false" [contextOrganisation]="contextOrganisationFor(data)" [contextScope]="contextScope"></app-top-five>
                }
            </div>
        }
    </article>
</ng-template>
<!-- the EPM scorecard in brief: its summary, headline figures, and the bottleneck when a role is overloaded -->
<ng-template #epmSlideTpl>
    <article class="carousel-slide" [style.--section]="MODEL_COLOURS.MODEL_COLOR">
        <header class="slide-head">
            <span class="slide-swatch" aria-hidden="true"></span>
            <h3>EPM summary</h3>
        </header>
        @if (epmOverview.loading) {
            <div class="slide-status" role="status"><mat-spinner diameter="22"></mat-spinner><span>Working out the EPM scorecard…</span></div>
        } @else if (epmOverview.error) {
            <p class="slide-status error" role="alert">{{ epmOverview.error }}</p>
        } @else if (epmOverview.data; as data) {
            <p class="slide-scope">{{ data.workspaceName }} · {{ data.scopeDescription }}</p>
            <p class="slide-key"><mat-icon aria-hidden="true">insights</mat-icon><span [innerHTML]="data.epm.summary"></span></p>
            <div class="epm-tiles" aria-label="EPM headline figures">
                @for (h of data.epm.headlines; track h.label) {
                    <div [class]="'epm-tile ' + h.tone">
                        <span class="label">{{ h.label }}</span>
                        <strong>{{ h.value }}</strong>
                        <span class="detail">{{ h.detail }}</span>
                    </div>
                }
            </div>
            @if (data.epm.bottleneck?.overloaded) {
                <aside class="slide-comment warn"><mat-icon aria-hidden="true">warning</mat-icon><span [innerHTML]="data.epm.bottleneck?.text"></span></aside>
            }
            @if (data.epmDataGaps.length) {
                <div class="slide-text">
                    <p><b>To make these figures more reliable</b>, fill in:</p>
                    <ul class="slide-list">
                        @for (gap of data.epmDataGaps.slice(0, 3); track gap) { <li [innerHTML]="gap"></li> }
                    </ul>
                </div>
            }
            <div class="slide-actions">
                <button mat-stroked-button color="accent" type="button" (click)="openLens('overview')"><mat-icon>dashboard</mat-icon>Open the overview</button>
            </div>
        }
    </article>
</ng-template>

<!-- what to do first: the top recommendations from every report, a few to act on now and then the largest of the rest -->
<ng-template #recommendationsSlideTpl>
    <article class="carousel-slide" [style.--section]="MODEL_COLOURS.RED">
        <header class="slide-head">
            <span class="slide-swatch" aria-hidden="true"></span>
            <h3>What to do first</h3>
        </header>
        @if (epmOverview.loading) {
            <div class="slide-status" role="status"><mat-spinner diameter="22"></mat-spinner><span>Working out the recommendations…</span></div>
        } @else if (epmOverview.error) {
            <p class="slide-status error" role="alert">{{ epmOverview.error }}</p>
        } @else if (epmOverview.data; as data) {
            <p class="slide-scope">{{ data.workspaceName }} · {{ data.scopeDescription }} · from the workforce and systems reports and the EPM scorecard</p>
            @if (epmOverview.recommendations.length) {
                <ol class="slide-recommendations">
                    @for (r of epmOverview.recommendations; track r.key) {
                        <li [class.urgent]="r.urgent">
                            <span class="priority" [matTooltip]="r.why">{{ r.priority }}</span>
                            <div class="what">
                                <div class="chips">
                                    <span [class]="'lever ' + r.lever">{{ leverLabels[r.lever] }}</span>
                                    @if (r.urgent) { <span class="now">Act now</span> }
                                </div>
                                <strong class="with-context">{{ r.title }} @if (r.lever !== 'data' && linqset?.organisation && epmOverview.scope) {<app-context-graph-button [organisation]="linqset?.organisation" [scope]="epmOverview.scope!" [request]="{recommendation: r}" [name]="r.title"></app-context-graph-button>}</strong>
                                @if (recommendationSize(r); as size) { <span class="size">{{ size }}</span> }
                            </div>
                            <button mat-stroked-button color="accent" type="button" (click)="openLens(r.lens, r.focus)">See in {{ lensLabel(r.lens).toLowerCase() }}</button>
                        </li>
                    }
                </ol>
                <div class="slide-actions">
                    <button mat-button color="accent" type="button" (click)="openLens('overview')">
                        {{ moreRecommendations ? moreRecommendations + ' more in the overview' : 'Open the overview' }}
                    </button>
                </div>
            } @else {
                <p class="slide-text">Nothing stands out: roles have the capacity for their work and information moves between systems automatically.</p>
            }
        }
    </article>
</ng-template>

<app-popup-carousel
    [(open)]="carouselOpen"
    [slides]="slides"
    [startIndex]="startAt"
    [wrap]="true"
    title="Insights"
    [subtitle]="carouselSubtitle"
></app-popup-carousel>

<div class="container">
    @if (linqset && !summary) {
        <mat-card appearance="outlined" class="overview-header">
            <mat-card-content>
                <div class="header-row">
                    <p class="header-description" [innerHTML]="linqset.description"></p>

                    <span class="buttons-space">
                        <button mat-stroked-button (click)="generatePdf()" color="accent" [disabled]="isGeneratingPdf()">
                            <mat-icon>picture_as_pdf</mat-icon>Download PDF
                        </button>
                        <button mat-stroked-button (click)="openCarousel()" color="accent" [tooltip]="['View carousel of insights']">
                            <mat-icon>view_carousel</mat-icon>View carousel
                        </button>
                    </span>
                </div>

                @if (linqset.hasModelNodes) {
                    <div id="model-heading">
                        <app-model-heading [linqset]="linqset" [trigger]="trigger"></app-model-heading>
                    </div>
                }
                @if (linqset?.insights?.processingErrors && linqset.insights.processingErrors.length > 0) {
                    <div class="processing-errors" role="alert">
                        <mat-icon>error_outline</mat-icon>
                        <div>
                            @for(error of linqset?.insights.processingErrors; track error) {
                                <p>{{error}}</p>
                            }
                        </div>
                    </div>
                }
                @if (pdfMessage && pdfMessage.length > 0) {
                    <div class="pdf-status">
                        <p><b>PDF generation. This may take some time, please wait: </b>{{pdfMessage}}</p>
                        @if (isGeneratingPdf()) {
                            <mat-progress-bar mode="indeterminate"></mat-progress-bar>
                        }
                    </div>
                }

            </mat-card-content>
        </mat-card>
        <mat-accordion>
            <mat-expansion-panel expanded="false" togglePosition="before">
                <mat-expansion-panel-header>
                    <mat-panel-title class="options-title">
                        <mat-icon>tune</mat-icon>Select sections and attributes to view
                    </mat-panel-title>
                </mat-expansion-panel-header>

                <div class="options-group">
                    <h3>Attributes</h3>
                    <mat-chip-listbox [(ngModel)]="filterOptions" selectable multiple (change)="onOptionsClick($event)" color="accent" aria-label="Attributes">
                        @for (o of getAllFilterOptions(); track o) {
                            <mat-chip-option [value]="o" color="accent">{{o}}</mat-chip-option>
                        }
                    </mat-chip-listbox>
                </div>
                <div class="options-group">
                    <h3>Sections</h3>
                    <mat-chip-listbox [(ngModel)]="sectionOptions" selectable multiple (change)="onSectionsClick($event)" color="accent" aria-label="Sections">
                        @for (o of getSectionOptions(); track o) {
                            <mat-chip-option [value]="o" color="accent">{{o}}</mat-chip-option>
                        }
                    </mat-chip-listbox>
                </div>
                <div class="options-actions">
                    <button mat-stroked-button (click)="savePreferences()" color="accent" [disabled]="disableSave">Save preferences</button>
                </div>
            </mat-expansion-panel>
        </mat-accordion>
    }
    <div id="overview-section">
        @if (comparison) {
            <h3 class="subheading">{{comparison.workspaceName}} node numbers</h3>
            <div class="numbers-row">
                <app-node-numbers [canSelect]="false" [insights]="comparisonInsights" [trigger]="trigger" (selectTypeEvent)="gotoSection($event)"></app-node-numbers>
            </div>
        }

        @if (linqset) {
            <h3 class="subheading">{{linqset.workspaceName}} node numbers</h3>
            <div class="numbers-row">
                <app-node-numbers [canSelect]="!summary" [insights]="insights" [trigger]="trigger" (selectTypeEvent)="gotoSection($event)"></app-node-numbers>
                @if (!summary) {
                    <span class="view-toggles">
                        <mat-slide-toggle [(ngModel)]="showAsTables">Show as tables</mat-slide-toggle>
                        <mat-slide-toggle [(ngModel)]="includePercentages" (change)="togglePercentages()">Show percentages</mat-slide-toggle>
                    </span>
                }
            </div>
        }
    </div>

    @if (summary && section) {
        <div [id]="'section-' + section.type" class="section-container">
            <div class="heading-container" [style.border-left-color]="section.colour">
                @if (section.nodeType) {
                    <app-node-svg [type]="section.nodeType" [noText]="true" [height]="28"/>
                }
                <h2 class="section-title">{{section.title}}</h2>
            </div>
            <app-insight-cards [cards]="section.visibleCards"></app-insight-cards>
            @if (hiddenCardCount(section) > 0) {
                <button mat-button color="accent" class="zero-cards-toggle" data-testid="zero-cards-toggle" (click)="toggleZeroCards()">
                    {{showZeroCards?'Hide zero values':'Show ' + hiddenCardCount(section) + ' zero value' + (hiddenCardCount(section) === 1?'':'s')}}
                </button>
            }

            @if (section.fields.length > 0) {
                <div class="table-grid">
                    @for (t of section.fields; track t.title) {
                        <app-top-five [title]="t.title" [subTitle]="t.subTitle" [rows]="t.rows" (selectRowEvent)="selectNode($event)" [colour]="t.colour" [showAsTable]="showAsTables" [includePercentages]="t.includePercentages" [includeUnits]="false" [contextOrganisation]="contextOrganisationFor(section)" [contextScope]="contextScope"></app-top-five>
                    }
                </div>
            }

        </div>
    }@else {

        @for (s of sections; track s.type) {
            <div [id]="'section-' + s.type" class="section-container">
                <div class="heading-container" [style.border-left-color]="s.colour">
                    @if (s.nodeType) {
                        <app-node-svg [type]="s.nodeType" [noText]="true" [height]="28"/>
                    }
                    <h2 class="section-title">{{s.title}}</h2>
                </div>
                <app-insight-cards [cards]="s.cards"></app-insight-cards>

                <div class="description-container">
                    @for (d of s.description; track d) {
                        <span [innerHTML]="d"></span>
                    }
                </div>
                <div class="comment-container" [class.has-comment]="s.comment && s.comment.length > 0">
                    @if (s.comment && s.comment.length > 0) {
                        <p class="comment-text" [innerHTML]="s.htmlComment"></p>
                        <button mat-icon-button matTooltip="Edit section comment" aria-label="Edit comment for section" color="accent" (click)="addComment(s)" [id]="'button-' + s.type"><mat-icon>edit</mat-icon></button>
                    }@else {
                        <button mat-button (click)="addComment(s)" color="accent" [id]="'button-' + s.type" matTooltip="The comment is saved in the filter">
                            <mat-icon>add_comment</mat-icon>Add a comment for this section
                        </button>
                    }
                </div>

                @if (linqset && (s.treemaps.length > 0 || s.pieData.length > 0 || s.type === INSIGHTS_SECTION.ACTIONS || s.type === INSIGHTS_SECTION.OVERVIEW)) {
                    <div class="chart-grid">
                        @for (treemap of s.treemaps; track treemap.heading) {
                            <div class="wide chart-pair">
                                <mat-card appearance="outlined" class="chart-card">
                                    <h4 class="card-title">{{treemap.heading}}</h4>
                                    @if (treemap.data2 && treemap.data2.length > 0) {
                                        <h4 class="chart-subheading">{{linqset.workspaceName}}</h4>
                                    }
                                    <app-treemap [data]="treemap.data1" [isCurrency]="treemap.currency"></app-treemap>
                                </mat-card>
                                @if (treemap.data2 && treemap.data2.length > 0) {
                                    <mat-card appearance="outlined" class="chart-card">
                                        <h4 class="card-title">{{treemap.heading}}</h4>
                                        <h4 class="chart-subheading">{{comparison.workspaceName}}</h4>
                                        <app-treemap [data]="treemap.data2" [isCurrency]="treemap.currency"></app-treemap>
                                    </mat-card>
                                }
                            </div>
                        }
                        @for (d of s.bubbleData; track d.heading) {
                            <mat-card appearance="outlined" class="chart-card wide">
                                <h4 class="card-title">{{d.heading}}</h4>
                                <app-bubble-chart [nodeData]="d.data" [axislabels]="d.labels" [maxValue]="d.maxValue" [size]="['100%', '380px']"></app-bubble-chart>
                            </mat-card>
                        }
                        @for (d of s.pieData; track d.heading) {
                            <mat-card appearance="outlined" class="chart-card">
                                <h4 class="card-title">{{d.heading}}</h4>
                                <div class="chart-body">
                                    <app-pie-chart [title]="d.heading" [data]="d.data" [height]="280" [isCurrency]="d.currency" [hasComparison]="comparison?true:false"></app-pie-chart>
                                </div>
                            </mat-card>
                        }
                        @if (s.type === INSIGHTS_SECTION.ACTIONS) {
                            <mat-card appearance="outlined" class="chart-card wide">
                                <h4 class="card-title">Action types</h4>
                                <div class="table-scroll">
                                    <app-action-types [insights]="linqset?.insights" [comparison]="comparison?.insights"></app-action-types>
                                </div>
                            </mat-card>
                        }
                        @if (s.type === INSIGHTS_SECTION.OVERVIEW) {
                            @if (isOrganisationInsights()) {
                                @if (isFieldSelected(INSIGHTS_FIELD.COST)) {
                                    <mat-card appearance="outlined" class="chart-card wide">
                                        <h4 class="card-title">Organisation costs</h4>
                                        <div class="table-scroll">
                                            <app-allocations-table [insights]="insights" [comparisonInsights]="comparisonInsights" [comparisonName]="comparison?.workspaceName" type="cost" [tableOnly]="true"></app-allocations-table>
                                        </div>
                                    </mat-card>
                                }
                                <!-- only when there is some carbon to show, in this workspace or the one compared -->
                                @if (isFieldSelected(INSIGHTS_FIELD.CARBON) && hasCarbon()) {
                                    <mat-card appearance="outlined" class="chart-card wide">
                                        <h4 class="card-title">Organisation carbon</h4>
                                        <div class="table-scroll">
                                            <app-allocations-table [insights]="insights" [comparisonInsights]="comparisonInsights" [comparisonName]="comparison?.workspaceName" type="carbon" [tableOnly]="true"></app-allocations-table>
                                        </div>
                                    </mat-card>
                                }
                            }
                        }
                    </div>
                }
                @if (s.fields.length > 0) {
                    <div class="table-grid">
                        @for (t of s.fields; track t.title) {
                            <app-top-five [title]="t.title" [subTitle]="t.subTitle" [rows]="t.rows" (selectRowEvent)="selectNode($event)" [colour]="t.colour" [showAsTable]="showAsTables" [includePercentages]="t.includePercentages" [includeUnits]="false" [contextOrganisation]="contextOrganisationFor(s)" [contextScope]="contextScope"></app-top-five>
                        }
                    </div>
                }
            </div>
        }
    }
</div>
